/**
 * The brand's reusable Personas (`BrandPersona`, managed in Brand Knowledge).
 *
 * The list is ≤ the per-brand `brand_personas` gauge (default 100), so it is loaded in
 * full and searched client-side. `refreshOnFocus` refetches when the window regains
 * focus: personas are created in Brand Knowledge in ANOTHER tab (the conversation editor
 * is local state with no draft path), and without it a just-created persona would not
 * appear in the picker until a reload.
 */
import { useEffect, useMemo, useState } from 'react';
import type { BrandPersona } from '@traaker/shared';
import { api } from '../../services/api';

export type BrandPersonaListItem = BrandPersona & { linkedConversations: number };

export function useBrandPersonas(
  brandId: string | undefined,
  opts: { enabled?: boolean; refreshOnFocus?: boolean } = {},
) {
  const { enabled = true, refreshOnFocus = false } = opts;
  const [personas, setPersonas] = useState<BrandPersonaListItem[]>([]);
  // `false` until the first response: an empty list must not read as « no persona ».
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!enabled || !brandId) return;
    let cancelled = false;
    const load = () => {
      api.get<BrandPersonaListItem[]>(`/brands/${brandId}/brand-personas`)
        .then((list) => { if (!cancelled) { setPersonas(list ?? []); setLoaded(true); } })
        .catch(() => { if (!cancelled) setLoaded(true); /* keep whatever we had */ });
    };
    load();
    if (!refreshOnFocus) return () => { cancelled = true; };
    window.addEventListener('focus', load);
    return () => { cancelled = true; window.removeEventListener('focus', load); };
  }, [brandId, enabled, refreshOnFocus]);

  const byId = useMemo(
    () => Object.fromEntries(personas.map((p) => [p.brandPersonaId, p])) as Record<string, BrandPersonaListItem>,
    [personas],
  );

  return { personas, byId, loaded };
}
